import { Link } from "react-router-dom";
import { Upload } from "lucide-react";

type LegendRow = {
  dotClass: string;
  labelBarClass: string;
  valueBarClass: string;
};

const DEFAULT_LEGEND: LegendRow[] = [
  {
    dotClass: "bg-emerald-500/50",
    labelBarClass: "w-[125px]",
    valueBarClass: "w-[60px]",
  },
  {
    dotClass: "bg-amber-500/50",
    labelBarClass: "w-[106px]",
    valueBarClass: "w-[60px]",
  },
  {
    dotClass: "bg-orange-500/45",
    labelBarClass: "w-[120px]",
    valueBarClass: "w-[60px]",
  },
  {
    dotClass: "bg-rose-500/50 opacity-80",
    labelBarClass: "w-[106px]",
    valueBarClass: "w-[60px]",
  },
];

type Props = {
  ctaTo: string;
  ctaLabel?: string;
  legendRows?: LegendRow[];
};

export function PieChartEmptyContent({
  ctaTo,
  ctaLabel = "Upload your Receivables file to start",
  legendRows = DEFAULT_LEGEND,
}: Props) {
  return (
    <div className="mt-4 flex flex-col items-stretch justify-between gap-6 sm:flex-row sm:items-center">
      <div className="relative mx-auto h-[165px] w-[165px] shrink-0 sm:mx-0">
        <div
          className="h-full w-full rounded-full opacity-90"
          style={{
            background:
              "conic-gradient(from -90deg, rgba(34,197,94,0.45) 0deg 140deg, rgba(234,179,8,0.45) 140deg 230deg, rgba(249,115,22,0.45) 230deg 300deg, rgba(244,63,94,0.45) 300deg 360deg)",
          }}
          aria-hidden
        />
        <div
          className="absolute inset-[31px] rounded-full bg-white"
          aria-hidden
        />
      </div>

      <div className="relative flex min-h-[165px] min-w-0 flex-1 items-center">
        <div className="w-full space-y-2.5 px-1 sm:px-2">
          {legendRows.map((row, index) => (
            <div
              key={index}
              className="flex items-center justify-between gap-6"
            >
              <div className="flex min-w-0 items-center gap-2">
                <span
                  className={`h-2.5 w-2.5 shrink-0 rounded-full ${row.dotClass}`}
                  aria-hidden
                />
                <span
                  className={`h-[18px] shrink rounded-full bg-slate-500/30 ${row.labelBarClass}`}
                  aria-hidden
                />
              </div>
              <span
                className={`h-[13px] shrink-0 rounded-full bg-slate-500/30 ${row.valueBarClass}`}
                aria-hidden
              />
            </div>
          ))}
        </div>

        <Link
          to={ctaTo}
          className="absolute left-1/2 top-1/2 z-10 inline-flex h-16 max-w-[calc(100%-1rem)] -translate-x-1/2 -translate-y-1/2 items-center gap-2 rounded-lg border border-[#3b82f6] bg-white px-4 text-center text-sm font-medium text-[#155dfc] shadow-sm sm:px-6"
        >
          <Upload className="h-6 w-6 shrink-0" aria-hidden />
          <span className="leading-tight">{ctaLabel}</span>
        </Link>
      </div>
    </div>
  );
}

type CardProps = Props & {
  title: string;
};

export function PieChartEmptyCard({ title, ...contentProps }: CardProps) {
  return (
    <section className="rounded-[14px] border border-[#e8ecf1] bg-white p-4 shadow-[0px_1px_1.5px_rgba(0,0,0,0.06)]">
      <h2 className="text-base font-semibold leading-6 text-[#0f172a]">
        {title}
      </h2>
      <PieChartEmptyContent {...contentProps} />
    </section>
  );
}
